import { useCallback, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { Lang } from "@/lib/translations";
import { translateSegmentsToEn, translateSegmentsToEs } from "@/lib/routes-i18n";

export const SITE_URL = "https://egconstructionsd.com";

/** True when the pathname belongs to the Spanish tree (/es or /es/...). */
export function isEsPath(pathname: string): boolean {
  return pathname === "/es" || pathname.startsWith("/es/");
}

/**
 * Removes the /es prefix and translates Spanish slugs back to the English
 * (language-agnostic) path. Always returns a path starting with "/".
 */
export function stripLangPrefix(pathname: string): string {
  if (!isEsPath(pathname)) return pathname || "/";
  const rest = pathname.slice(3);
  const base = rest === "" || rest === "/" ? "/" : rest;
  return translateSegmentsToEn(base);
}

/** Builds the path for a given language from any path (EN or ES). */
export function localizedPath(pathname: string, lang: Lang): string {
  const base = stripLangPrefix(pathname) || "/";
  if (lang === "en") return base;
  const es = translateSegmentsToEs(base);
  return es === "/" ? "/es" : `/es${es}`;
}


/**
 * Language is derived from the URL: /es/* is Spanish, everything else English.
 * No local state — navigation is the single source of truth.
 */
export function useLang() {
  const location = useLocation();
  const navigate = useNavigate();

  const lang: Lang = isEsPath(location.pathname) ? "es" : "en";
  const basePath = useMemo(() => stripLangPrefix(location.pathname), [location.pathname]);

  const toPath = useCallback(
    (path: string, target: Lang = lang) => localizedPath(path, target),
    [lang],
  );

  const toggleLang = useCallback(() => {
    const next: Lang = lang === "en" ? "es" : "en";
    navigate(`${localizedPath(basePath, next)}${location.search}${location.hash}`);
  }, [lang, basePath, navigate, location.search, location.hash]);

  return { lang, basePath, toggleLang, toPath };
}

/**
 * Keeps the active language while navigating: intercepts clicks on internal
 * links that lack the /es prefix while browsing in Spanish and rewrites them.
 * Mounted once inside the router so every page benefits without touching links.
 */
export function LanguageLinkPrefix() {
  const location = useLocation();
  const navigate = useNavigate();
  const es = isEsPath(location.pathname);

  useEffect(() => {
    if (!es) return;
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = (e.target as HTMLElement | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      if (anchor.target && anchor.target !== "_self") return;
      if (anchor.hasAttribute("download")) return;
      const href = anchor.getAttribute("href") || "";
      if (!href.startsWith("/") || href.startsWith("//")) return;
      const [pathPart] = href.split(/[?#]/);
      if (isEsPath(pathPart)) return;
      // Skip static assets served from /public
      if (/\.[a-z0-9]{2,5}$/i.test(pathPart)) return;
      e.preventDefault();
      e.stopPropagation();
      navigate(`${localizedPath(pathPart, "es")}${href.slice(pathPart.length)}`);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [es, navigate]);

  return null;
}
